import * as support from "./support";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { Autocomplete, Box, Paper, Stack, Typography } from "@mui/material";
import * as arrays from "common/arrays";
import * as ids from "common/ids";
import Component from "components/Component";
import IconButton from "components/common/IconButton";
import TextField from "components/common/TextField";
import * as labels from "config.labels";
import { useCallback, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import * as endorsementsStore from "store/endorsements";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const DND_TYPE_T_AND_C = "T_AND_C";

const Entry = ({ index, value, onUpdate, onDelete, onDrop }) => {
  const [, dragRef, dragPreviewRef] = useDrag(() => ({
    type: DND_TYPE_T_AND_C,
    item: { index },
  }));

  const [, dropRef] = useDrop(
    () => ({
      accept: DND_TYPE_T_AND_C,
      drop: (item) => onDrop(item.index),
    }),
    [onDrop]
  );

  return (
    <Stack
      direction={"row"}
      spacing={2}
      justifyContent={"flex-start"}
      alignItems={"center"}
      ref={(el) => {
        dragRef(el);
        dropRef(el);
        dragPreviewRef(el);
      }}
    >
      <Typography
        width={20}
        align={"right"}
        fontWeight={"bold"}
        sx={{ padding: 2 }}
      >
        {index + 1}
      </Typography>
      {value.type === "CUSTOM" ? (
        <TextField
          hiddenLabel
          multiline
          value={value?.text ?? ""}
          onChange={(e) =>
            onUpdate({
              ...value,
              text: e.target.value,
            })
          }
        />
      ) : (
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-start"}
          sx={{ width: "100%" }}
        >
          <Typography sx={{ padding: "12px" }}>{value?.text ?? ""}</Typography>
        </Stack>
      )}
      <Stack
        direction={"row"}
        spacing={0}
        justifyContent={"flex-start"}
        alignItems={"center"}
      >
        <IconButton icon={DeleteIcon} onClick={onDelete} />
        <DragIndicatorIcon />
      </Stack>
    </Stack>
  );
};

const SelectedTermsAndConditions = ({
  entries,
  updateEntry,
  deleteEntry,
  moveEntry,
}) => {
  return (
    <Stack direction={"column"} spacing={1}>
      {entries.map((entry, index) => (
        <Entry
          key={index}
          index={index}
          value={entry}
          onUpdate={updateEntry(entry.key)}
          onDelete={deleteEntry(entry.key)}
          onDrop={moveEntry(index)}
        />
      ))}
    </Stack>
  );
};

const StandardTermsAndConditionsSelector = ({ config, entries, addEntry }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const visibleKeys = (config?.defaultVisibleTermsAndConditions ?? []).filter(
    (key) => config?.termsAndConditions?.[key]?.key === key
  );
  const selectedKeys = new Set(entries?.map(({ key }) => key));
  const unselectedKeys = visibleKeys.filter((key) => !selectedKeys.has(key));

  const options = unselectedKeys.map(
    (key) => config?.termsAndConditions?.[key]
  );

  return (
    <Autocomplete
      value={null}
      onChange={(_, value, reason) => {
        if (reason === "selectOption") {
          addEntry(value);
        }
      }}
      options={options}
      getOptionKey={({ key }) => key}
      getOptionLabel={({ text }) => text}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translateLabel(labels.TERMS_AND_CONDITIONS_STANDARD_LABEL, {
            default: "Standard Terms & Conditions",
          })}
          variant={"filled"}
          InputLabelProps={{ shrink: true }}
        />
      )}
      autoHighlight
      disableClearable
      blurOnSelect
      fullWidth
    />
  );
};

const AvailableTermsAndConditions = ({ config, entries, addEntry }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  return (
    <Paper sx={{ width: "100%" }}>
      <Stack
        direction={"column"}
        spacing={2}
        sx={{
          padding: 2,
        }}
      >
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography variant={"subtitle1"}>
            {translateLabel(labels.TERMS_AND_CONDITIONS_AVAILABLE_TITLE, {
              default: "Available Terms & Conditions",
            })}
          </Typography>
        </Stack>
        <StandardTermsAndConditionsSelector
          config={config}
          entries={entries}
          addEntry={addEntry}
        />
        <Box>
          <IconButton
            icon={AddIcon}
            onClick={() => addEntry({ text: "" })}
            tooltip={translateLabel(
              labels.TERMS_AND_CONDITIONS_ADD_CUSTOM_TOOLTIP,
              {
                default: "Add Custom",
              }
            )}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export const TermsAndConditions = ({ layerId }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const dispatch = useDispatch();

  const { config } = support.usePolicyConfig({ layerId });

  const rawEntries = useSelector(endorsementsStore.selectInput)
    ?.termsAndConditionsByLayer?.[layerId];
  const entries = rawEntries ?? [];

  const setEntries = useCallback(
    (newEntries) =>
      dispatch(
        endorsementsStore.setLayerTermsAndConditions({
          layerId,
          termsAndConditions: newEntries,
        })
      ),
    [dispatch, layerId]
  );

  const addEntry = (value) => {
    const entry = value?.key
      ? {
          ...value,
        }
      : {
          ...value,
          type: "CUSTOM",
          key: ids.makeNewId(),
        };

    setEntries([...entries, entry]);
  };

  const updateEntry = (key) => (value) =>
    setEntries(
      entries.map((entry) =>
        entry.key === key ? { ...entry, ...value } : entry
      )
    );

  const deleteEntry = (key) => () =>
    setEntries(entries.filter((entry) => entry.key !== key));

  const moveEntry = (toIndex) => (fromIndex) =>
    setEntries(arrays.move(entries, { fromIndex, toIndex }));

  useEffect(() => {
    /* Set the default selected T&Cs on the layer if none are set. */
    if (
      rawEntries == null &&
      config?.defaultSelectedTermsAndConditions?.length
    ) {
      setEntries(
        config.defaultSelectedTermsAndConditions
          .map((key) => config?.termsAndConditions?.[key])
          .filter((x) => !!x)
      );
    }
  }, [rawEntries, config, setEntries]);

  return (
    <Component
      title={translateLabel(labels.TERMS_AND_CONDITIONS_TITLE, {
        default: "Terms & Conditions",
      })}
      subtitle={translateLabel(labels.TERMS_AND_CONDITIONS_SUBTITLE, {
        default: "",
      })}
    >
      <Stack direction={"column"} spacing={2}>
        <SelectedTermsAndConditions
          entries={entries}
          addEntry={addEntry}
          updateEntry={updateEntry}
          deleteEntry={deleteEntry}
          moveEntry={moveEntry}
        />
        <AvailableTermsAndConditions
          config={config}
          entries={entries}
          addEntry={addEntry}
        />
      </Stack>
    </Component>
  );
};

export default TermsAndConditions;
